import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import ButtonElement from '@/components/elements/Button/Button';
import TitleWithContent from '@/components/elements/TitleWithContent/TitleWithContent';
import { useGalleryMedia } from './useGalleryMedia';
import GalleryGrid from './GalleryGrid';
import CategoryCarouselSection from './CategoryCarouselSection';
import type { CategoryGroupTile, MediaItem } from './galleryMedia';

/** How many items show before "Load more images" is needed, for whichever filter is active. */
const PREVIEW_COUNT = 15;

/**
 * Matches TitleWithContent's own (inline, clamp-based) padding, so the filter pills and the
 * grid below the heading line up with it — Tailwind's `!important` config means a padding
 * class on the same element as this style would just win outright, so this is only ever used
 * on its own.
 */
const CONTENT_PADDING_STYLE: React.CSSProperties = {
  paddingLeft: 'clamp(.2rem, 6vw, 8rem)',
  paddingRight: 'clamp(.2rem, 6vw, 8rem)',
};

/**
 * The gallery's landing page: the same header format as every other page (TitleWithContent),
 * then a filterable masonry grid. Filtering is client-side, so switching category never
 * leaves the page or hides anything behind a click — whatever isn't tagged with a category
 * shows under "All Works" (the default filter) alongside everything else. Categories are
 * whatever actually appears on at least one item (Sanity "Gallery Category" documents, or a
 * local item's category — see galleryMedia.ts): there's no fixed list to fall back to, and an
 * unused category simply doesn't get a pill. Counts, filters and captions all come from real
 * data; nothing here is invented. Each category also keeps its own real page at
 * `/gallery/<slug>` (see GalleryCategoryPage) for direct links and search results, even though
 * these filter pills don't navigate there.
 */
const Gallery: React.FC = () => {
  const { mediaArray, status } = useGalleryMedia();
  const [activeSlug, setActiveSlug] = useState<string | null>(null);
  const [showAll, setShowAll] = useState(false);

  const { byCategorySlug, filters } = useMemo(() => {
    const byCategorySlug = new Map<string, MediaItem[]>();
    const titleBySlug = new Map<string, string>();

    for (const item of mediaArray) {
      if (!item.category) continue;
      const { slug, title } = item.category;
      titleBySlug.set(slug, title);
      const list = byCategorySlug.get(slug);
      if (list) list.push(item);
      else byCategorySlug.set(slug, [item]);
    }

    const filters = [
      { slug: null as string | null, title: 'All Works' },
      ...[...titleBySlug.entries()]
        .map(([slug, title]) => ({ slug: slug as string | null, title }))
        .sort((a, b) => a.title.localeCompare(b.title)),
    ];

    return { byCategorySlug, filters };
  }, [mediaArray]);

  const filteredItems = useMemo(
    () => (activeSlug === null ? mediaArray : byCategorySlug.get(activeSlug) ?? []),
    [activeSlug, mediaArray, byCategorySlug]
  );

  // "All Works" pulls every category with more than one photo out into its own full-width
  // carousel section (see CategoryCarouselSection), so the masonry grid below is just the
  // photos that aren't part of a multi-photo category — the whole point of a filter is to pick
  // a category, so there's no reason to also see all of it, uncollapsed, before picking one.
  // Selecting a filter shows that one category's items individually in the grid, same as always
  // (no carousels — you're already inside that category).
  const { categoryGroups, ungroupedItems } = useMemo(() => {
    if (activeSlug !== null) return { categoryGroups: [] as CategoryGroupTile[], ungroupedItems: filteredItems };

    const groupedSlugsSeen = new Set<string>();
    const categoryGroups: CategoryGroupTile[] = [];
    const ungroupedItems: MediaItem[] = [];
    for (const item of filteredItems) {
      const category = item.category;
      const group = category ? byCategorySlug.get(category.slug) : undefined;

      if (category && group && group.length > 1) {
        if (groupedSlugsSeen.has(category.slug)) continue;
        const images = group.filter(
          (candidate): candidate is Extract<MediaItem, { kind: 'sanity-image' | 'image' }> =>
            candidate.kind === 'sanity-image' || candidate.kind === 'image'
        );
        if (images.length > 0) {
          groupedSlugsSeen.add(category.slug);
          categoryGroups.push({
            id: `group-${category.slug}`,
            kind: 'category-group',
            slug: category.slug,
            title: category.title,
            description: category.description,
            count: group.length,
            images,
          });
          continue;
        }
        // No images in this category to show (e.g. it's all videos) — fall through and show
        // its items individually instead of grouping.
      }

      ungroupedItems.push(item);
    }
    return { categoryGroups, ungroupedItems };
  }, [filteredItems, activeSlug, byCategorySlug]);

  const visibleItems = showAll ? ungroupedItems : ungroupedItems.slice(0, PREVIEW_COUNT);

  const selectFilter = (slug: string | null) => {
    setActiveSlug(slug);
    setShowAll(false);
  };

  return (
    <div className="2xl:px-40 xl:px-35 lg:px-30 md:px-20 px-3 my-[5rem]">
      <motion.div
        initial={{ opacity: 0, scaleX: 0 }}
        animate={{ opacity: 1, scaleX: 1 }}
        transition={{ duration: 0.5 }}
      >
        <TitleWithContent title="Our Work in Action" level={1} />
      </motion.div>

      {status === 'error' && (
        <p style={CONTENT_PADDING_STYLE} className="mt-6 text-gray-500">
          We couldn&apos;t load the gallery right now. Please try again later.
        </p>
      )}

      {filters.length > 1 && (
        <div style={CONTENT_PADDING_STYLE} className="mb-8 flex flex-wrap items-center gap-2">
          {filters.map((filter) => (
            <ButtonElement
              key={filter.slug ?? 'all'}
              btnText={filter.title}
              size="small"
              active={filter.slug === activeSlug}
              onClick={() => selectFilter(filter.slug)}
              className="text-xs sm:text-sm"
            />
          ))}
        </div>
      )}

      <div style={CONTENT_PADDING_STYLE}>
        {categoryGroups.length > 0 && (
          <div className="mb-6 flex flex-col gap-6 md:mb-8 md:gap-8">
            {categoryGroups.map((group) => (
              <CategoryCarouselSection key={group.id} group={group} />
            ))}
          </div>
        )}

        <GalleryGrid items={visibleItems} theme="dark" />

        {ungroupedItems.length > PREVIEW_COUNT && (
          <div className="mt-6 flex justify-center">
            <ButtonElement
              size="small"
              onClick={() => setShowAll((value) => !value)}
              btnText={
                <span className="inline-flex items-center gap-2">
                  {showAll ? 'Show less' : 'Load more images'}
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className={`transition-transform ${showAll ? 'rotate-180' : ''}`}
                  >
                    <path d="M6 9l6 6 6-6" />
                  </svg>
                </span>
              }
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default Gallery;
